import {
  ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useReducer,
} from "react";
import { ProductImage } from "../../../services/types/productImage.type";
import "./PhotosManager.css";
import plus from "../../../assets/icons/plus.png";
import { Icon } from "../../ui/images/Icon/Icon";
import { PhotoItem } from "../PhotoItem/PhotoItem";

// Интерфейс состояния, которым управляет редьюсер
export interface PhotosState {
  photos: ProductImage[];
  deletedPhotoIds: string[];
}

// Интерфейс пропсов PhotosManager
export interface PhotosManagerProps {
  photos: ProductImage[];
  productUuid?: string;
}

// Интерфейс, который экспортируется через ref
export interface PhotosManagerRef {
  getPhotosState: () => PhotosState;
}

// Действия редьюсера
type PhotoAction =
  | { type: "INITIALIZE"; payload: ProductImage[] }
  | { type: "ADD"; payload: File[] }
  | {
      type: "UPDATE_FIELD";
      index: number;
      field: keyof ProductImage;
      value: string;
    }
  | { type: "MOVE"; index: number; direction: "up" | "down" }
  | { type: "DELETE"; index: number };

// Функция генерации altText
const generateAltText = (imageType: string, position: number): string =>
  `Image ${imageType} at position ${position}`;

// Редьюсер, управляющий состоянием фотографий и списком удалённых ID
const photosReducer = (
  state: PhotosState,
  action: PhotoAction,
): PhotosState => {
  switch (action.type) {
    case "INITIALIZE":
      return { photos: action.payload, deletedPhotoIds: [] };
    case "ADD": {
      const newPhotos = action.payload.map((file, idx) => {
        const pos = state.photos.length + idx;
        return {
          file,
          altText: generateAltText("cover", pos),
          imageType: "cover",
          position: pos,
        };
      });
      return { ...state, photos: [...state.photos, ...newPhotos] };
    }
    case "UPDATE_FIELD": {
      const updated = [...state.photos];
      if (action.field === "imageType") {
        updated[action.index] = {
          ...updated[action.index],
          imageType: action.value,
          altText: generateAltText(
            action.value,
            updated[action.index].position,
          ),
        };
      } else {
        updated[action.index] = {
          ...updated[action.index],
          [action.field]: action.value,
        };
      }
      return { ...state, photos: updated };
    }
    case "MOVE": {
      const updated = [...state.photos];
      const newIndex =
        action.direction === "up" ? action.index - 1 : action.index + 1;
      if (newIndex < 0 || newIndex >= updated.length) return state;
      [updated[action.index], updated[newIndex]] = [
        updated[newIndex],
        updated[action.index],
      ];
      const finalPhotos = updated.map((photo, idx) => ({
        ...photo,
        position: idx,
        altText: generateAltText(photo.imageType, idx),
      }));
      return { ...state, photos: finalPhotos };
    }
    case "DELETE": {
      const photoToDelete = state.photos[action.index];
      const updated = state.photos
        .filter((_, idx) => idx !== action.index)
        .map((photo, idx) => ({
          ...photo,
          position: idx,
          altText: generateAltText(photo.imageType, idx),
        }));
      const newDeleted = photoToDelete.uuid
        ? [...state.deletedPhotoIds, photoToDelete.uuid]
        : state.deletedPhotoIds;
      return { photos: updated, deletedPhotoIds: newDeleted };
    }
    default:
      return state;
  }
};

// Компонент PhotosManager реализован с использованием forwardRef
export const PhotosManager = forwardRef<PhotosManagerRef, PhotosManagerProps>(
  ({ photos, productUuid }, ref) => {
    const [state, dispatch] = useReducer(photosReducer, {
      photos: photos || [],
      deletedPhotoIds: [],
    });

    // Экспонируем метод getPhotosState через ref
    useImperativeHandle(ref, () => ({
      getPhotosState: () => state,
    }));

    const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const files = Array.from(e.target.files);
        dispatch({ type: "ADD", payload: files });
      }
    };

    const updatePhotoField = (
      index: number,
      field: keyof ProductImage,
      value: string,
    ) => {
      dispatch({ type: "UPDATE_FIELD", index, field, value });
    };

    const movePhoto = (index: number, direction: "up" | "down") => {
      dispatch({ type: "MOVE", index, direction });
    };

    const deletePhoto = (index: number) => {
      dispatch({ type: "DELETE", index });
    };

    return (
      <div className="photos-manager-wrap">
        {state.photos.map((item, index) => (
          <PhotoItem
            key={index}
            index={index}
            photo={item}
            updatePhotoField={updatePhotoField}
            movePhoto={movePhoto}
            deletePhoto={deletePhoto}
          />
        ))}
        <label htmlFor="add-photo" className="photos-manager-add">
          <Icon icon={plus} width="2rem" />
          <input
            id="add-photo"
            className="photos-manager-input"
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
          />
        </label>
      </div>
    );
  },
);
